import { useEffect, useState, useCallback, useRef } from "react";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Landmark, Upload, AlertCircle, CheckCircle2, CopyX, ClipboardList, Brain,
  FileText, Trash2, X, ArrowDownCircle, ArrowUpCircle, Loader2, ShieldCheck,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import BankAccounts from "./BankAccounts";

const STATUS_STYLE = {
  "Needs Review": "bg-amber-50 text-amber-700 dark:bg-amber-500/10",
  "Classified": "bg-blue-50 text-blue-700 dark:bg-blue-500/10",
  "Posted": "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10",
  "Duplicate": "bg-slate-100 text-slate-600 dark:bg-slate-500/10",
  "Ignored": "bg-muted text-muted-foreground",
};

const TABS = ["Accounts", "Needs Review", "Classified", "Posted", "Duplicate", "Rules"];

function StatCard({ icon: Icon, label, value, accent, i, onClick, active }) {
  return (
    <div onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`rounded-2xl border bg-card p-4 kpi-enter transition-all ${onClick ? "cursor-pointer hover:shadow-sm" : ""} ${active ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
      style={{ animationDelay: `${i * 40}ms` }} data-testid={`bank-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${accent}`}><Icon className="w-4.5 h-4.5" strokeWidth={1.75} /></div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="font-heading text-xl font-bold tracking-tight mt-0.5">{value}</p>
    </div>
  );
}

export default function BankTransactions() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [statements, setStatements] = useState([]);
  const [rules, setRules] = useState([]);
  const [tab, setTab] = useState("Needs Review");
  const [reviewing, setReviewing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [account, setAccount] = useState("");
  const [accountId, setAccountId] = useState("");
  const [stmtBalance, setStmtBalance] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState({});
  const [refs, setRefs] = useState({ properties: [], tenants: [], categories: { income: [], expense: [] } });
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/bank/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/bank/transactions").then((r) => setRows(r.data)).catch(() => {});
    api.get("/bank/statements").then((r) => setStatements(r.data)).catch(() => {});
    api.get("/bank/rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/bank/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    Promise.all([
      api.get("/properties").catch(() => ({ data: [] })),
      api.get("/tenants").catch(() => ({ data: [] })),
      api.get("/bank/categories").catch(() => ({ data: { income: [], expense: [] } })),
    ]).then(([p, t, c]) => setRefs({
      properties: p.data, tenants: t.data.filter((x) => x.status === "Active"), categories: c.data,
    }));
  }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("File too large (max 6MB)."); return; }
    setUploading(true);
    try {
      const data = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const { data: r } = await api.post("/bank/upload", { filename: file.name, data, bank_account: account, account_id: accountId, current_balance: stmtBalance === "" ? null : Number(stmtBalance) });
      toast.success(`Imported ${r.imported} transaction(s): ${r.classified} ready, ${r.needs_review} to review, ${r.duplicates} duplicate(s) skipped.`);
      setTab(r.needs_review > 0 ? "Needs Review" : "Classified");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/bank/transactions/${id}/approve`);
      toast.success("Approved & posted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const ignore = async (id) => {
    try { await api.post(`/bank/transactions/${id}/ignore`); toast.success("Ignored"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const bulkApprove = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error("Select transactions first"); return; }
    try {
      const { data } = await api.post("/bank/transactions/approve-bulk", { ids });
      toast.success(`Posted ${data.posted}, skipped ${data.skipped}`);
      setSelected({});
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delStatement = async (id) => {
    try { await api.delete(`/bank/statements/${id}`); toast.success("Statement removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delRule = async (id) => {
    try { await api.delete(`/bank/rules/${id}`); toast.success("Rule removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const visible = rows.filter((r) => r.status === tab);
  const count = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div className="space-y-6" data-testid="page-banking">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Banking</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Landmark className="w-7 h-7 text-primary" /> Bank Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload a statement — we read, classify, and match transactions. Review anything unclear, then approve to post rent &amp; expenses.</p>
        </div>
      </div>

      {tab !== "Accounts" && summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard i={0} icon={AlertCircle} label="Needs Review" value={summary.needs_review} accent="bg-amber-50 text-amber-600 dark:bg-amber-500/10" onClick={() => { setTab("Needs Review"); setSelected({}); }} active={tab === "Needs Review"} />
          <StatCard i={1} icon={ClipboardList} label="Ready to Approve" value={summary.classified} accent="bg-blue-50 text-blue-600 dark:bg-blue-500/10" onClick={() => { setTab("Classified"); setSelected({}); }} active={tab === "Classified"} />
          <StatCard i={2} icon={CheckCircle2} label="Posted" value={summary.posted} accent="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10" onClick={() => { setTab("Posted"); setSelected({}); }} active={tab === "Posted"} />
          <StatCard i={3} icon={CopyX} label="Duplicates Skipped" value={summary.duplicates} accent="bg-slate-100 text-slate-600 dark:bg-slate-500/10" onClick={() => { setTab("Duplicate"); setSelected({}); }} active={tab === "Duplicate"} />
          <StatCard i={4} icon={Brain} label="Learned Rules" value={summary.rules_count} accent="bg-violet-50 text-violet-600 dark:bg-violet-500/10" onClick={() => { setTab("Rules"); setSelected({}); }} active={tab === "Rules"} />
        </div>
      )}

      {tab !== "Accounts" && canWrite && (
        <section className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Upload className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">Upload Bank Statement</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Supports <b>PDF, CSV, and Excel</b>. Transactions are extracted, categorized, matched to properties/tenants, and duplicates are skipped automatically.</p>
          <div className="flex flex-wrap items-center gap-3">
            <select value={accountId} onChange={(e) => { setAccountId(e.target.value); const a = accounts.find((x) => x.id === e.target.value); setAccount(a ? `${a.bank_name} ${a.account_name} ${a.last4 ? "…" + a.last4 : ""}`.trim() : ""); }}
              data-testid="upload-account-select" className="rounded-xl border border-border bg-background p-2.5 text-sm min-w-[200px]">
              <option value="">Select account (optional)…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} {a.account_name} {a.last4 ? `····${a.last4}` : ""}</option>)}
            </select>
            <input value={account} onChange={(e) => setAccount(e.target.value)} data-testid="bank-account-input"
              placeholder="…or type account label"
              className="flex-1 min-w-[160px] rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <input type="number" value={stmtBalance} onChange={(e) => setStmtBalance(e.target.value)} data-testid="upload-balance-input"
              placeholder="Statement balance" className="w-40 rounded-xl border border-border bg-background p-2.5 text-sm" />
            <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="bank-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bank-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Processing…" : "Choose Statement"}
            </button>
          </div>
          {statements.length > 0 && (
            <div className="mt-4 border-t border-border pt-3 space-y-1.5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Recent uploads</p>
              {statements.slice(0, 5).map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm py-1" data-testid={`bank-statement-${s.id}`}>
                  <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{s.filename}</span>
                    <span className="text-xs text-muted-foreground">· {s.transaction_count} txns · {String(s.uploaded_at).slice(0, 10)}</span>
                  </span>
                  {canWrite && <button onClick={() => delStatement(s.id)} data-testid={`bank-del-statement-${s.id}`} className="text-muted-foreground hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        {TABS.map((f) => (
          <button key={f} onClick={() => { setTab(f); setSelected({}); }} data-testid={`bank-tab-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${tab === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
            {f}{f === "Accounts" ? ` (${accounts.length})` : f !== "Rules" ? ` (${count(f)})` : ` (${rules.length})`}
          </button>
        ))}
      </div>

      {tab === "Accounts" ? (
        <BankAccounts refs={refs} canWrite={canWrite} />
      ) : tab === "Rules" ? (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="bank-rules-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Keyword</th>
                  <th className="text-left font-semibold px-4 py-3">Category</th>
                  <th className="text-left font-semibold px-4 py-3">Direction</th>
                  <th className="text-left font-semibold px-4 py-3">Property / Tenant</th>
                  <th className="text-left font-semibold px-4 py-3">Uses</th>
                  {canWrite && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No learned rules yet. Approve reviewed transactions to teach the system.</td></tr>}
                {rules.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/40" data-testid={`bank-rule-${r.id}`}>
                    <td className="px-4 py-3 font-medium">{r.pattern}</td>
                    <td className="px-4 py-3">{r.category}</td>
                    <td className="px-4 py-3 capitalize">{r.direction}</td>
                    <td className="px-4 py-3 text-muted-foreground">{[r.property_name, r.tenant_name].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3">{r.hits || 1}</td>
                    {canWrite && <td className="px-4 py-3 text-right"><button onClick={() => delRule(r.id)} data-testid={`bank-del-rule-${r.id}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {tab === "Classified" && canWrite && visible.length > 0 && (
            <div className="flex justify-end">
              <button onClick={bulkApprove} data-testid="bank-bulk-approve" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors">
                <CheckCircle2 className="w-4 h-4" /> Approve selected ({Object.values(selected).filter(Boolean).length})
              </button>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    {tab === "Classified" && canWrite && <th className="px-3 py-3 w-8" />}
                    <th className="text-left font-semibold px-4 py-3">Date</th>
                    <th className="text-left font-semibold px-4 py-3">Description</th>
                    <th className="text-right font-semibold px-4 py-3">Amount</th>
                    <th className="text-left font-semibold px-4 py-3">Category</th>
                    <th className="text-left font-semibold px-4 py-3">Property / Tenant</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    {canWrite && <th className="text-right font-semibold px-4 py-3">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No transactions in this view.</td></tr>}
                  {visible.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`bank-txn-${r.id}`}>
                      {tab === "Classified" && canWrite && (
                        <td className="px-3 py-3"><input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected({ ...selected, [r.id]: e.target.checked })} data-testid={`bank-select-${r.id}`} /></td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{r.date}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {r.direction === "income" ? <ArrowDownCircle className="w-4 h-4 text-emerald-500 shrink-0" /> : <ArrowUpCircle className="w-4 h-4 text-red-400 shrink-0" />}
                          <span className="truncate max-w-[260px]">{r.description}</span>
                        </div>
                        {r.possible_duplicate && <span className="text-[11px] text-amber-600">Possible duplicate</span>}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold ${r.direction === "income" ? "text-emerald-600" : ""}`}>{r.direction === "income" ? "+" : "−"}{money(r.amount)}</td>
                      <td className="px-4 py-3">{r.category || <span className="text-muted-foreground italic">Uncategorized</span>}</td>
                      <td className="px-4 py-3 text-muted-foreground">{[r.property_name, r.tenant_name].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[r.status] || "bg-muted"}`}>{r.status}</span></td>
                      {canWrite && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {r.status === "Posted" ? <span className="text-xs text-muted-foreground">→ {r.posted_collection === "payments" ? "Rent" : "Expenses"}</span> : (
                            <div className="flex items-center justify-end gap-1.5">
                              <button onClick={() => setReviewing(r)} data-testid={`bank-review-${r.id}`} className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors">Review</button>
                              {r.status === "Classified" && <button onClick={() => approve(r.id)} data-testid={`bank-approve-${r.id}`} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors">Approve</button>}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ReviewDialog item={reviewing} refs={refs} onClose={() => setReviewing(null)}
        onDone={() => { setReviewing(null); load(); }} onApprove={approve} onIgnore={ignore} />
    </div>
  );
}

function ReviewDialog({ item, refs, onClose, onDone, onApprove, onIgnore }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (item) setForm({
      direction: item.direction, category: item.category || "", property_id: item.property_id || "",
      tenant_id: item.tenant_id || "", unit: item.unit || "", notes: item.notes || "",
    });
  }, [item]);
  if (!item) return null;

  const cats = form.direction === "income" ? refs.categories.income : refs.categories.expense;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (thenApprove) => {
    const prop = refs.properties.find((p) => p.id === form.property_id);
    const ten = refs.tenants.find((t) => t.id === form.tenant_id);
    const payload = {
      direction: form.direction, category: form.category,
      property_id: form.property_id, property_name: prop?.name || "",
      tenant_id: form.tenant_id, tenant_name: ten?.name || "",
      unit: form.unit, notes: form.notes,
      lease_id: ten?.lease_id || item.lease_id || "",
    };
    try {
      await api.put(`/bank/transactions/${item.id}`, payload);
      if (thenApprove) { await onApprove(item.id); onClose(); }
      else { toast.success("Saved"); onDone(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bank-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Review Transaction — {item.direction === "income" ? "+" : "−"}{money(item.amount)}</DialogTitle>
          <DialogDescription>Confirm the category and matching, then approve to post it to your records.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl bg-muted/50 p-3 space-y-1">
            <p><span className="text-muted-foreground">Date:</span> {item.date}</p>
            <p><span className="text-muted-foreground">Description:</span> {item.description}</p>
            {item.bank_account && <p><span className="text-muted-foreground">Account:</span> {item.bank_account}</p>}
            {item.classification_source && <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Classified by {item.classification_source} ({item.confidence}%)</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</label>
              <select value={form.direction} onChange={(e) => { set("direction", e.target.value); set("category", ""); }} data-testid="bank-review-direction"
                className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="bank-review-category"
                className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">— Select —</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Property</label>
              <select value={form.property_id} onChange={(e) => set("property_id", e.target.value)} data-testid="bank-review-property"
                className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">— None —</option>
                {refs.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tenant</label>
              <select value={form.tenant_id} onChange={(e) => set("tenant_id", e.target.value)} data-testid="bank-review-tenant"
                className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">— None —</option>
                {refs.tenants.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.property_name} {t.unit}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Notes</label>
            <input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="bank-review-notes"
              className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Optional note" />
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <button onClick={() => onIgnore(item.id).then(onClose)} data-testid="bank-review-ignore" className="px-3 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted">Ignore</button>
          <div className="flex gap-2 flex-1 justify-end">
            <button onClick={() => save(false)} data-testid="bank-review-save" className="px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted">Save</button>
            <button onClick={() => save(true)} data-testid="bank-review-approve" className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">Approve &amp; Post</button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
